/**
 * LES GESTES DE CHAQUE MÉTIER — données pures, lues par l'écran ET par l'essai.
 *
 * ⚠️ RÈGLE DU FONDATEUR, 21/09/2026 : « on ne peut pas être dans l'uniformité,
 * car chaque mode est un métier avec ses propres exigences, ses propres flux
 * métier, ses propres attentes — chacun sera un OS », puis « il faut des
 * boutons d'actions pour chaque mode ».
 *
 * ⚠️ CE QUI ÉTAIT UNIFORME N'ÉTAIT PAS LA MATIÈRE, C'ÉTAIT LE GESTE.
 * `sec.mode_compteur` tient déjà des compteurs PAR MÉTIER, et ils diffèrent
 * vraiment — le fiscaliste compte ses assiettes manquantes, l'urbaniste ses
 * contrôles non traités. Mais aucun écran ne proposait d'AGIR : sept métiers
 * arrivaient sur la même grille de tuiles et devaient chercher leur propre
 * travail dans le rail.
 *
 * ⚠️⚠️ CHAQUE GESTE EST ANCRÉ AU RAIL DE SON MODE, ET C'EST VÉRIFIÉ EN BASE.
 * Un bouton qui vise une rubrique que le mode n'a pas est un bouton MORT — il
 * n'échoue pas, il ouvre un écran que l'habilitation referme, ou rien. C'est
 * exactement le défaut que le fondateur a vu sur le cockpit du notaire : six
 * libellés distincts, une seule destination.
 * `tests/chaque-mode-a-ses-gestes.test.ts` confronte cette table à
 * `sec.mode_rubrique` et rougit à la première action orpheline.
 *
 * ⚠️ IL EST DANS `src/partage/` POUR UNE RAISON MESURÉE. Cette table vivait
 * dans `bureau-metier.tsx` ; l'essai devait donc importer un `.tsx`, et
 * `tsconfig.tests.json` ne configure pas `--jsx` :
 *     error TS6142: … was resolved to '…tsx', but '--jsx' is not set.
 * Or ce rouge N'APPARAÎT PAS dans les essais — `vitest` transpile avec esbuild,
 * qui ne type pas. Il ne se serait montré qu'au typecheck du pré-vol, c'est-à-
 * dire au moment de déployer. **Un essai vert qui casse la bascule est pire
 * qu'un essai rouge.** Les gardes d'écran de ce dépôt lisent les `.tsx` en
 * TEXTE ; ce qui doit être importé vit ici.
 */
export interface Geste {
  libelle: string;
  /** La rubrique qu'il ouvre. Confrontée au rail du mode par l'essai. */
  rubrique: string;
  aide: string;
  /**
   * ⚠️ LA PERMISSION QU'IL EXIGE — et elle doit EXISTER dans
   * `sec.role_permission`. Une ressource inventée ne lève aucune erreur :
   * `autoriser` rend simplement « non accordé », le bouton reste fermé POUR
   * TOUJOURS, et son motif accuse l'habilitation de l'agent au lieu de la
   * faute de frappe. `tests/gestes-gov-class.test.ts` confronte chaque couple
   * au catalogue réel des permissions.
   */
  permission: { ressource: string; operation: string };
}

export const GESTES_PAR_MODE: Record<string, Geste[]> = {
  /* Il lève, il borne, il fait entendre les riverains. Il ne dresse pas d'acte. */
  geometre: [
    { libelle: 'Déposer un levé', rubrique: 'geometre', aide: 'un levé rend une parcelle opposable' , permission: { ressource: 'cad.releve', operation: 'creer' } },
    { libelle: 'Recueillir les riverains', rubrique: 'geometre', aide: 'l’adjudication contradictoire' , permission: { ressource: 'cad.releve', operation: 'modifier' } },
    { libelle: 'Consulter les contrôles', rubrique: 'anomalies', aide: 'ce que le contrôle qualité a relevé sur vos levés', permission: { ressource: 'cad.controle', operation: 'lire' } },
    // ⚠️ LA RUBRIQUE A CHANGÉ DE NOM, PAS LE GESTE. Le plan cadastral est entré
    // dans la carte le 22/09/2026 : le tableau d'assemblage et le découpage en
    // feuilles y sont des panneaux. Laissé sur `assemblage`, ce geste pointerait
    // vers une clé que le catalogue ne connaît plus — un bouton qui navigue
    // « quelque part », donc qui a l'air de marcher.
    { libelle: 'Assembler un plan', rubrique: 'carte', aide: 'planches et feuilles imprimables', permission: { ressource: 'cad.parcelle', operation: 'lire' } },
  ],
  /* Il cherche l'assiette : ce qui devrait être imposé et ne l'est pas. */
  fiscal: [
    { libelle: 'Rapprocher une assiette', rubrique: 'fiscal', aide: 'relier une parcelle à son objet fiscal' , permission: { ressource: 'lien.objet_fiscal', operation: 'creer' } },
    { libelle: 'Consulter le livre foncier', rubrique: 'livre', aide: 'la feuille d’un titre et ses mentions' , permission: { ressource: 'fon.feuille_fonciere', operation: 'lire' } },
    { libelle: 'Parcourir le territoire', rubrique: 'territoire', aide: 'divisions et collectivités' , permission: { ressource: 'ter.division', operation: 'lire' } },
    { libelle: 'Assembler un plan de tournée', rubrique: 'carte', aide: 'planches imprimables pour le terrain', permission: { ressource: 'cad.parcelle', operation: 'lire' } },
  ],
  /* Il contrôle l'usage du sol : servitudes, restrictions, projets. */
  urbanisme: [
    { libelle: 'Consulter les contrôles', rubrique: 'anomalies', aide: 'les contrôles relevés sur le territoire', permission: { ressource: 'cad.controle', operation: 'lire' } },
    { libelle: 'Étudier une prospective', rubrique: 'prospective', aide: 'scénarios d’aménagement' , permission: { ressource: 'ter.division', operation: 'lire' } },
    { libelle: 'Ouvrir le jumeau numérique', rubrique: 'jumeau', aide: 'la ville telle qu’elle est modélisée' , permission: { ressource: 'cad.batiment', operation: 'lire' } },
    { libelle: 'Consulter les restrictions', rubrique: 'foncier', aide: 'servitudes et charges qui pèsent sur un fonds', permission: { ressource: 'fon.restriction', operation: 'lire' } },
  ],
  /* Elle reçoit le public, constate, et transmet. */
  collectivite: [
    { libelle: 'Recevoir une demande', rubrique: 'guichet', aide: 'le guichet, et le récépissé dû à l’usager' , permission: { ressource: 'fon.demande', operation: 'creer' } },
    { libelle: 'Dresser une attestation', rubrique: 'attestations', aide: 'l’acte communal qui constate une cession' , permission: { ressource: 'fon.attestation_cession', operation: 'creer' } },
    { libelle: 'Suivre un dossier', rubrique: 'affaires', aide: 'où en est chaque procédure ouverte' , permission: { ressource: 'fon.affaire', operation: 'lire' } },
    { libelle: 'Écrire un courrier', rubrique: 'courrier', aide: 'notifier une partie, opposablement' , permission: { ressource: 'doc.document', operation: 'creer' } },
  ],
  /* Il regarde le marché et ce qu'un bien vaut vraiment. */
  investisseur: [
    { libelle: 'Vérifier un bien', rubrique: 'foncier', aide: 'la parcelle, sa surface et son état au registre', permission: { ressource: 'cad.parcelle', operation: 'lire' } },
    { libelle: 'Parcourir le marché', rubrique: 'immobilier', aide: 'transactions enregistrées' , permission: { ressource: 'lien.transaction', operation: 'lire' } },
    { libelle: 'Examiner une copropriété', rubrique: 'coproprietes', aide: 'lots, tantièmes, parties communes' , permission: { ressource: 'cop.copropriete', operation: 'lire' } },
    { libelle: 'Ouvrir le jumeau numérique', rubrique: 'jumeau', aide: 'l’environnement d’un bien' , permission: { ressource: 'cad.batiment', operation: 'lire' } },
  ],
  /* Il décide, il valide, il arbitre. */
  etat: [
    { libelle: 'Décider un levé', rubrique: 'geometre', aide: 'valider ou rejeter, avec motif' , permission: { ressource: 'cad.releve', operation: 'valider' } },
    { libelle: 'Valider une mutation', rubrique: 'mutations', aide: 'la seconde voix qui écrit au registre' , permission: { ressource: 'fon.mutation', operation: 'valider' } },
    { libelle: 'Traiter une anomalie', rubrique: 'anomalies', aide: 'ce que les contrôles ont relevé' , permission: { ressource: 'cad.controle', operation: 'traiter' } },
    { libelle: 'Consulter le livre foncier', rubrique: 'livre', aide: 'la feuille d’un titre et ses mentions' , permission: { ressource: 'fon.feuille_fonciere', operation: 'lire' } },
  ],
};

export const TITRES: Record<string, string> = {
  geometre: 'Bureau du géomètre', fiscal: 'Bureau fiscal', urbanisme: 'Bureau de l’urbaniste',
  collectivite: 'Bureau de la collectivité', investisseur: 'Bureau de l’investisseur',
  etat: 'Bureau de l’État', notaire: 'Bureau du notaire',
};
